import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Check, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface OutputHeaderProps {
  title: string;
  formFields?: Array<{ label: string; [key: string]: any }>;
  formData?: Record<string, any>;
  className?: string;
  canEditTitle?: boolean;
  onTitleSave?: (newTitle: string) => Promise<void> | void;
  belowTitleSlot?: React.ReactNode;
}

export function OutputHeader({
  title,
  formFields = [],
  formData = {},
  className = "",
  canEditTitle = false,
  onTitleSave,
  belowTitleSlot,
}: OutputHeaderProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [localTitle, setLocalTitle] = React.useState(title);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  const handleSave = async () => {
    const trimmed = (localTitle || "").trim();
    if (!trimmed || trimmed === title) {
      setIsEditing(false);
      return;
    }
    try {
      setIsSaving(true);
      await onTitleSave?.(trimmed);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };
  const visibleFields = Array.isArray(formFields)
    ? formFields.filter((f) => {
        const label = (f && (f as any).label) as string | undefined;
        if (!label) return false;
        const val = (formData as any)[label];
        return val !== undefined && val !== null && String(val) !== "";
      })
    : [];

  return (
    <div className={className}>
      {title && title.trim().length > 0 && (
        <div className="mb-2 pt-4 flex items-center gap-2">
          {isEditing ? (
            <>
              <Input
                value={localTitle}
                onChange={(e) => setLocalTitle(e.target.value)}
                className="h-9 max-w-md"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSave();
                  }
                  if (e.key === "Escape") setIsEditing(false);
                }}
                autoFocus
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleSave}
                disabled={isSaving}
                className="h-9 px-2"
                title="Save title"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="h-9 px-2"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              {canEditTitle && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
                  onClick={() => setIsEditing(true)}
                  title="Edit title"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {belowTitleSlot && <div className="mb-2">{belowTitleSlot}</div>}

      {visibleFields.length > 0 && (
        <div className="mb-4">
          <Accordion type="single" collapsible>
            <AccordionItem value="inputs">
              <AccordionTrigger className="text-gray-600">
                View inputs
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {visibleFields.map((field) => {
                    const label = (field as any).label as string;
                    const value = (formData as any)[label];
                    const isFileArray =
                      Array.isArray(value) &&
                      value.length > 0 &&
                      (value[0]?.url || value[0]?.name);
                    return (
                      <div
                        key={label}
                        className="bg-white border border-gray-200 rounded-md p-3"
                      >
                        <div className="text-xs uppercase text-gray-500 mb-1">
                          {label}
                        </div>
                        {isFileArray ? (
                          <div className="flex flex-col gap-2">
                            {value.map((f: any) => (
                              <div
                                key={f.id || f.url}
                                className="flex items-center gap-3 text-sm"
                              >
                                <div className="h-10 w-10 bg-gray-100 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
                                  {f.type?.startsWith("image/") ? (
                                    <img
                                      src={f.url}
                                      alt={f.name}
                                      className="h-10 w-10 object-cover"
                                    />
                                  ) : (
                                    <div className="text-xs text-gray-500 font-medium">
                                      PDF
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <a
                                    href={f.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 hover:underline font-medium block truncate"
                                    title={f.name || f.url}
                                  >
                                    {f.name
                                      ? f.name.length > 30
                                        ? `${f.name.substring(0, 27)}...`
                                        : f.name
                                      : f.url}
                                  </a>
                                  <div className="text-xs text-gray-500">
                                    Click to download
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900 whitespace-pre-wrap">
                            {String(value)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </div>
  );
}
